import React from 'react';
import { useAuth } from '../../hooks/useAuth';
import AuthForm from './AuthForm';

interface AuthGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showLoading?: boolean;
}

const Spinner: React.FC<{ label?: string }> = ({ label }) => (
  <div className="min-h-dvh bg-gray-50 flex items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 rounded-full border-2 border-gray-200 border-t-blue-600 animate-spin" />
      {label && <p className="text-sm text-gray-400">{label}</p>}
    </div>
  </div>
);

const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  fallback,
  showLoading = true,
}) => {
  const { isAuthenticated, isLoading, user, tokenReady } = useAuth();

  if ((isLoading || (isAuthenticated && !tokenReady)) && showLoading) {
    return <Spinner label={isLoading ? undefined : 'Preparing secure access…'} />;
  }

  if (!isAuthenticated || !user) {
    if (fallback) return <>{fallback}</>;
    return (
      <div className="min-h-dvh bg-gray-50 flex items-center justify-center p-4">
        <AuthForm />
      </div>
    );
  }

  if (!tokenReady && showLoading) {
    return <Spinner label="Preparing secure access…" />;
  }

  return <>{children}</>;
};

export default AuthGuard;
